import { Row, Col, Table, Button, Tag, Tooltip, Image, Modal, Form, Radio, Alert, Checkbox, Space } from "antd";
import SubCategorySelect from "../../components/select/SubCategorySelect";
import { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import notfound from "../../assets/images/no-img.png";
import { EyeIcon, PencilIcon, Trash2, Upload } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { buildProductListSearch } from "./productNav";
import VehicleManufacturerSelect from "../../components/select/VehicleManufacturerSelect";
import VehicleSelect from "../../components/select/VehicleSelect";
import { DeleteOutlined } from "@ant-design/icons";

const VEHICLE_MAPPING_OPTIONS = [
  { label: "All Vehicles", value: "all", short: "A", color: "success" },
  { label: "By Manufacturers", value: "manufacturers", short: "M", color: "warning" },
  { label: "Specific Models", value: "specific", short: "S", color: "processing" },
];

function IncompleteProducts({ search = "", brandId = null, categoryId = null, segmentId = null, vehicleManufacturerId = null, vehicleId = null, vehicleAddedFilter = null, vehicleMappingFilter = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { request } = useRequest();

  // Carry the active tab (and current list state) into the pages opened from
  // this tab so their Back / Close action returns here.
  const tabSearch = buildProductListSearch(location.search, "incomplete");

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  // SubCategory modal
  const [subCatModalVisible, setSubCatModalVisible] = useState(false);
  const [subCategoryId, setSubCategoryId] = useState(null);
  const [subCatLoading, setSubCatLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [mixedCategoryError, setMixedCategoryError] = useState(false);

  // Vehicle modal
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [mixedSegmentError, setMixedSegmentError] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [vehicleOption, setVehicleOption] = useState("all");
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [vehicleModelMappings, setVehicleModelMappings] = useState([]);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [currentManufacturerId, setCurrentManufacturerId] = useState(null);
  const [currentManufacturerName, setCurrentManufacturerName] = useState(null);
  const [currentVehicleIds, setCurrentVehicleIds] = useState([]);
  const [vehicleOptionsMap, setVehicleOptionsMap] = useState({});

  // Model selection modal (nested inside vehicle modal)
  const [modelEditVisible, setModelEditVisible] = useState(false);
  const [modelEditRowIndex, setModelEditRowIndex] = useState(null);
  const [modelEditModels, setModelEditModels] = useState([]);
  const [modelEditSelected, setModelEditSelected] = useState([]);
  const [modelEditLoading, setModelEditLoading] = useState(false);
  const [modelsCache, setModelsCache] = useState({});
  const [modelNamesCache, setModelNamesCache] = useState({});

  // Resolve missing vehicle names when modal opens with pre-existing specific mappings
  useEffect(() => {
    if (!vehicleModalVisible || vehicleOption !== "specific") return;
    const needsResolution = vehicleModelMappings.some((m) => m.vehicle_id && !m.vehicle_name);
    if (!needsResolution) return;

    const manufacturerIds = [...new Set(vehicleModelMappings.map((m) => m.manufacturer_id).filter(Boolean))];
    const vehicleNameMap = {};
    let completed = 0;

    manufacturerIds.forEach((manufacturerId) => {
      request({
        url: `${apiPath.commonVehicle}?manufacturer_id=${manufacturerId}`,
        method: "GET",
        onSuccess: (res) => {
          const vehicles = res?.data?.data || [];
          vehicles.forEach((v) => { vehicleNameMap[v._id] = v.en_name; });
          completed++;
          if (completed === manufacturerIds.length) {
            setVehicleModelMappings((prev) =>
              prev.map((m) => ({
                ...m,
                vehicle_name: m.vehicle_name || vehicleNameMap[m.vehicle_id] || null,
              }))
            );
          }
        },
        onError: () => { completed++; },
      });
    });
  }, [vehicleModalVisible]);

  const fetchData = (pag) => {
    setLoading(true);
    let url = `${apiPath.listTempProduct}?status=incomplete&page=${pag.current}&limit=${pag.pageSize}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (brandId) url += `&brand_id=${brandId}`;
    if (categoryId) url += `&category_id=${categoryId}`;
    if (segmentId) url += `&segment_id=${segmentId}`;
    if (vehicleManufacturerId) url += `&vehicle_manufacturer_id=${vehicleManufacturerId}`;
    if (vehicleId) url += `&vehicle_id=${vehicleId}`;
    if (vehicleAddedFilter) url += `&vehicle_added=${vehicleAddedFilter}`;
    if (vehicleMappingFilter) url += `&vehicle_mapping_option=${vehicleMappingFilter}`;
    request({
      url,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
        setPagination((prev) => ({
          ...prev,
          current: pag.current,
          pageSize: pag.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: () => setLoading(false),
    });
  };

  useEffect(() => {
    const resetPag = { current: 1, pageSize: pagination.pageSize };
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(resetPag);
  }, [refresh, search, brandId, categoryId, segmentId, vehicleManufacturerId, vehicleId, vehicleAddedFilter, vehicleMappingFilter]);

  const handleTableChange = (pag) => {
    const newPag = { current: pag.current, pageSize: pag.pageSize };
    setPagination((prev) => ({ ...prev, ...newPag }));
    fetchData(newPag);
  };

  const handleSendToQC = () => {
    if (selectedRowKeys.length === 0) {
      ShowToast("Please select at least one product", Severty.ERROR);
      return;
    }
    Modal.confirm({
      title: "Send to QC",
      content: `Send ${selectedRowKeys.length} product(s) to QC review?`,
      onOk: () => {
        request({
          url: apiPath.bulkSendToQcTempProduct,
          method: "POST",
          data: { ids: selectedRowKeys },
          onSuccess: () => {
            ShowToast("Products sent to QC successfully", Severty.SUCCESS);
            setSelectedRowKeys([]);
            setRefresh((prev) => !prev);
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.message || "Failed to send to QC", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleDelete = (ids) => {
    const isSingle = ids.length === 1;
    Modal.confirm({
      title: "Delete Product(s)",
      content: isSingle
        ? "Are you sure you want to delete this product? This cannot be undone."
        : `Are you sure you want to delete ${ids.length} product(s)? This cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => {
        request({
          url: apiPath.bulkDeleteTempProduct,
          method: "POST",
          data: { ids },
          onSuccess: () => {
            ShowToast(isSingle ? "Product deleted" : `${ids.length} products deleted`, Severty.SUCCESS);
            setSelectedRowKeys([]);
            setRefresh((prev) => !prev);
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.message || "Failed to delete", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleBulkSubCategory = () => {
    if (selectedRowKeys.length === 0) {
      ShowToast("Please select at least one product", Severty.ERROR);
      return;
    }
    const selectedProducts = list.filter((p) => selectedRowKeys.includes(p._id));
    const uniqueCategoryIds = [...new Set(selectedProducts.map((p) => p.category?._id).filter(Boolean))];
    setSubCategoryId(null);
    if (uniqueCategoryIds.length > 1) {
      setMixedCategoryError(true);
      setSelectedCategoryId(null);
    } else {
      setMixedCategoryError(false);
      setSelectedCategoryId(uniqueCategoryIds[0] || null);
    }
    setSubCatModalVisible(true);
  };

  const handleSubCatSave = () => {
    setSubCatLoading(true);
    request({
      url: apiPath.bulkSubcategoryTempProduct,
      method: "POST",
      data: { ids: selectedRowKeys.length > 0 ? selectedRowKeys : [], sub_category_id: subCategoryId },
      onSuccess: () => {
        setSubCatLoading(false);
        ShowToast("Sub-category updated successfully", Severty.SUCCESS);
        setSubCatModalVisible(false);
        setRefresh((prev) => !prev);
      },
      onError: (err) => {
        setSubCatLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to update sub-category", Severty.ERROR);
      },
    });
  };

  const handleBulkVehicle = () => {
    const selectedProducts = list.filter((p) => selectedRowKeys.includes(p._id));
    const uniqueSegmentIds = [...new Set(selectedProducts.map((p) => p.segment?._id).filter(Boolean))];

    // Reset transient add-form state
    setCurrentManufacturerId(null);
    setCurrentManufacturerName(null);
    setCurrentVehicleIds([]);
    setVehicleOptionsMap({});
    setModelsCache({});
    setModelNamesCache({});

    if (selectedProducts.length === 1) {
      const product = selectedProducts[0];
      setVehicleOption(product.vehicle_mapping_option || "all");
      setSelectedManufacturers((product.vehicle_manufacturer_ids || []).map((id) => id?.toString ? id.toString() : id));
      setVehicleModelMappings(
        (product.vehicle_model_mappings || []).map((m) => ({
          manufacturer_id: m.manufacturer_id,
          manufacturer_name: m.manufacturer_name || null,
          vehicle_id: m.vehicle_id,
          vehicle_name: m.vehicle_name || null,
          model_selection: m.model_selection || "all",
          specific_models: m.specific_models || [],
        }))
      );
    } else {
      setVehicleOption("all");
      setSelectedManufacturers([]);
      setVehicleModelMappings([]);
    }

    if (uniqueSegmentIds.length > 1) {
      setMixedSegmentError(true);
      setSelectedSegmentId(null);
    } else {
      setMixedSegmentError(false);
      setSelectedSegmentId(uniqueSegmentIds[0] || null);
    }
    setVehicleModalVisible(true);
  };

  const handleAddVehicleMapping = () => {
    if (!currentManufacturerId) {
      ShowToast("Please select a manufacturer", Severty.ERROR);
      return;
    }
    if (currentVehicleIds.length === 0) {
      ShowToast("Please select at least one vehicle", Severty.ERROR);
      return;
    }
    const existingVehicleIds = new Set(vehicleModelMappings.map((m) => m.vehicle_id));
    const newVehicleIds = currentVehicleIds.filter((vid) => !existingVehicleIds.has(vid));
    if (newVehicleIds.length === 0) {
      ShowToast("All selected vehicles are already added", Severty.WARNING);
      return;
    }
    const newMappings = newVehicleIds.map((vehicleId) => ({
      manufacturer_id: currentManufacturerId,
      manufacturer_name: currentManufacturerName,
      vehicle_id: vehicleId,
      vehicle_name: vehicleOptionsMap[vehicleId] || null,
      model_selection: "all",
      specific_models: [],
    }));
    setVehicleModelMappings((prev) => [...prev, ...newMappings]);
    setCurrentManufacturerId(null);
    setCurrentManufacturerName(null);
    setCurrentVehicleIds([]);
    const skipped = currentVehicleIds.length - newVehicleIds.length;
    if (skipped > 0) {
      ShowToast(`${newVehicleIds.length} vehicle(s) added, ${skipped} duplicate(s) skipped`, Severty.SUCCESS);
    } else {
      ShowToast(`${newVehicleIds.length} vehicle(s) added successfully`, Severty.SUCCESS);
    }
  };

  const openModelEditModal = (rowIndex) => {
    const row = vehicleModelMappings[rowIndex];
    setModelEditRowIndex(rowIndex);
    setModelEditSelected(row.specific_models || []);
    setModelEditVisible(true);
    const vehicleId = row.vehicle_id;
    if (modelsCache[vehicleId]) {
      setModelEditModels(modelsCache[vehicleId]);
      return;
    }
    setModelEditLoading(true);
    request({
      url: `${apiPath.commonVehicleModel}?vehicle_id=${vehicleId}${selectedSegmentId ? `&segment_id=${selectedSegmentId}` : ""}`,
      method: "GET",
      onSuccess: (res) => {
        const modelList = res?.data?.data || [];
        setModelEditModels(modelList);
        setModelsCache((prev) => ({ ...prev, [vehicleId]: modelList }));
        const namesMap = {};
        modelList.forEach((m) => { namesMap[m._id] = m.en_name; });
        setModelNamesCache((prev) => ({ ...prev, ...namesMap }));
        setModelEditLoading(false);
      },
      onError: () => setModelEditLoading(false),
    });
  };

  const handleModelEditConfirm = () => {
    setVehicleModelMappings((prev) =>
      prev.map((m, i) => {
        if (i !== modelEditRowIndex) return m;
        return {
          ...m,
          model_selection: modelEditSelected.length === 0 ? "all" : "specific",
          specific_models: modelEditSelected,
        };
      })
    );
    setModelEditVisible(false);
    setModelEditRowIndex(null);
    setModelEditSelected([]);
  };

  const handleModelEditCancel = () => {
    setModelEditVisible(false);
    setModelEditRowIndex(null);
    setModelEditSelected([]);
  };

  const getModelDisplayText = (row) => {
    if (row.model_selection === "all" || !row.specific_models?.length) return null;
    const names = row.specific_models.map((id) => modelNamesCache[id]).filter(Boolean);
    if (names.length === row.specific_models.length) return names.join(", ");
    return `${row.specific_models.length} model(s) selected`;
  };

  const handleVehicleSave = () => {
    if (vehicleOption === "manufacturers" && selectedManufacturers.length === 0) {
      ShowToast("Please select at least one manufacturer", Severty.ERROR);
      return;
    }
    if (vehicleOption === "specific" && vehicleModelMappings.length === 0) {
      ShowToast("Please add at least one vehicle mapping", Severty.ERROR);
      return;
    }
    const payload = {
      ids: selectedRowKeys.length > 0 ? selectedRowKeys : [],
      vehicle_mapping_option: vehicleOption,
      vehicle_manufacturer_ids: vehicleOption === "manufacturers" ? selectedManufacturers : [],
      vehicle_model_mappings: vehicleOption === "specific" ? vehicleModelMappings : [],
    };
    setVehicleLoading(true);
    request({
      url: apiPath.bulkVehicleTempProduct,
      method: "POST",
      data: payload,
      onSuccess: () => {
        setVehicleLoading(false);
        ShowToast("Vehicle mapping updated successfully", Severty.SUCCESS);
        setVehicleModalVisible(false);
        setRefresh((prev) => !prev);
      },
      onError: (err) => {
        setVehicleLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to update vehicle mapping", Severty.ERROR);
      },
    });
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image",
      render: (image_url) => <Image width={50} height={50} src={image_url || notfound} />,
    },
    {
      title: "Part Number",
      dataIndex: "part_number",
      key: "part_number",
      render: (val) => val || "-",
    },
    {
      title: "English Name",
      dataIndex: "en_name",
      key: "en_name",
      render: (val) => val || "-",
    },
    {
      title: "Brand",
      key: "brand",
      render: (_, { brand }) => brand?.en_name || "-",
    },
    {
      title: "Category & Sub Cat",
      key: "category",
      render: (_, { category, sub_category }) => (
        <div>
          <div>{category?.en_name || "-"}</div>
          {sub_category?.en_name && (
            <div style={{ fontSize: 13, color: "#888", marginTop: 1 }} className="capitalize">{sub_category.en_name}</div>
          )}
        </div>
      ),
    },
    {
      title: "Segment",
      key: "segment",
      render: (_, { segment }) => segment?.en_name || "-",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (val) => (val ? `₹${val}` : "-"),
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      render: (val) => (val ? moment(val).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Action",
      fixed: "right",
      render: (_, record) => (
        <div className="flex gap-1">
          <Tooltip title="View" color="purple">
            <Button onClick={() => navigate(`/product/temp/view/${record._id}${tabSearch}`)}>
              <EyeIcon size={15} />
            </Button>
          </Tooltip>
          <Tooltip title="Edit" color="blue">
            <Button onClick={() => navigate(`/product/temp/edit/${record._id}${tabSearch}`)}>
              <PencilIcon size={15} />
            </Button>
          </Tooltip>
          <Tooltip title="Delete" color="red">
            <Button danger onClick={() => handleDelete([record._id])} style={{ borderColor: "#ff4d4f", color: "#ff4d4f" }}>
              <Trash2 size={15} color="#ff4d4f" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <style>{`
        tr.row-mapping-all td:first-child { border-left: 4px solid #52c41a !important; }
        tr.row-mapping-manufacturers td:first-child { border-left: 4px solid #fa8c16 !important; }
        tr.row-mapping-specific td:first-child { border-left: 4px solid #2f54eb !important; }
      `}</style>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => navigate(`/product/import${tabSearch}`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-emerald-500 text-emerald-600 rounded-md bg-transparent hover:bg-emerald-500 hover:text-white transition-all duration-200"
        >
          <Upload size={14} /> Import Products
        </button>
        <button
          onClick={handleBulkSubCategory}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-violet-500 text-violet-600 rounded-md bg-transparent hover:bg-violet-500 hover:text-white transition-all duration-200"
        >
          Update Subcategory {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
        </button>
        <button
          disabled={selectedRowKeys.length === 0}
          onClick={handleBulkVehicle}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-orange-500 text-orange-600 rounded-md bg-transparent hover:bg-orange-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Update Vehicle {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
        </button>
        <button
          disabled={selectedRowKeys.length === 0}
          onClick={handleSendToQC}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-blue-500 text-blue-600 rounded-md bg-transparent hover:bg-blue-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Send to QC {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
        </button>
        <button
          disabled={selectedRowKeys.length === 0}
          onClick={() => handleDelete(selectedRowKeys)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-red-500 text-red-600 rounded-md bg-transparent hover:bg-red-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 size={14} /> Delete {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
        </button>
      </div>

      <div className="table-responsive customPagination">
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={list}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            defaultPageSize: 20,
            pageSizeOptions: ["10", "20", "30", "50", "100"],
          }}
          onChange={handleTableChange}
          className="ant-border-space"
          onRow={(record) => {
            const mapping = record.vehicle_mapping_option;
            return mapping ? { className: `row-mapping-${mapping}` } : {};
          }}
        />
      </div>

      {/* SubCategory Modal */}
      <Modal
        title="Update Sub-Category"
        open={subCatModalVisible}
        onCancel={() => setSubCatModalVisible(false)}
        onOk={mixedCategoryError ? undefined : handleSubCatSave}
        confirmLoading={subCatLoading}
        okText="Update"
        okButtonProps={{ disabled: mixedCategoryError }}
        footer={
          mixedCategoryError ? (
            <Button onClick={() => setSubCatModalVisible(false)}>Close</Button>
          ) : undefined
        }
      >
        {mixedCategoryError ? (
          <Alert
            type="warning"
            showIcon
            message="Mixed Categories Selected"
            description="The selected products belong to different categories. Sub-category can only be updated when all selected products are from the same category. Please select products of the same category and try again."
          />
        ) : (
          <>
            <p style={{ marginBottom: 12 }}>
              Updating <strong>{selectedRowKeys.length}</strong> selected product(s)
            </p>
            <SubCategorySelect
              categoryId={selectedCategoryId}
              value={subCategoryId}
              onChange={setSubCategoryId}
              shouldFetch={!!selectedCategoryId}
            />
          </>
        )}
      </Modal>

      {/* Vehicle Modal */}
      <Modal
        title={(() => {
          if (selectedRowKeys.length === 1) {
            const product = list.find((p) => p._id === selectedRowKeys[0]);
            return product?.part_number
              ? `Update Vehicle Mapping - ${product.part_number}`
              : "Update Vehicle Mapping";
          }
          return "Update Vehicle Mapping";
        })()}
        open={vehicleModalVisible}
        onCancel={() => setVehicleModalVisible(false)}
        onOk={mixedSegmentError ? undefined : handleVehicleSave}
        confirmLoading={vehicleLoading}
        okText="Update"
        okButtonProps={{ disabled: mixedSegmentError }}
        footer={
          mixedSegmentError ? (
            <Button onClick={() => setVehicleModalVisible(false)}>Close</Button>
          ) : undefined
        }
        width={1000}
        styles={{ body: { maxHeight: "65vh", overflowY: "auto", paddingRight: 4 } }}
      >
        {mixedSegmentError ? (
          <Alert
            type="warning"
            showIcon
            message="Mixed Segments Selected"
            description="The selected products belong to different segments. Vehicle mapping only works correctly within a single segment. Please select products of the same segment and try again."
          />
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Vehicle Mapping Option :</span>
              <Radio.Group value={vehicleOption} onChange={(e) => setVehicleOption(e.target.value)}>
                <Radio value="all">All Vehicles</Radio>
                <Radio value="manufacturers">By Manufacturers</Radio>
                <Radio value="specific">Specific Models</Radio>
              </Radio.Group>
            </div>

            <Form layout="vertical">
              {vehicleOption === "manufacturers" && (
                <Form.Item label="Select Manufacturers">
                  <VehicleManufacturerSelect
                    segmentId={selectedSegmentId}
                    multiple={true}
                    value={selectedManufacturers}
                    onChange={setSelectedManufacturers}
                  />
                </Form.Item>
              )}

              {vehicleOption === "specific" && (
                <>
                  <div className="bg-gray-50 border rounded-lg p-3 mb-3">
                    <Row gutter={[8, 12]}>
                      <Col span={20}>
                        <Form.Item label="Select Manufacturer" style={{ marginBottom: 0 }}>
                          <VehicleManufacturerSelect
                            segmentId={selectedSegmentId}
                            value={currentManufacturerId}
                            onChange={(val, option) => {
                              setCurrentManufacturerId(val);
                              setCurrentManufacturerName(option?.children || option?.label || val);
                              setCurrentVehicleIds([]);
                              setVehicleOptionsMap({});
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4} style={{ display: "flex", alignItems: "flex-end" }}>
                        <Button
                          type="primary"
                          onClick={handleAddVehicleMapping}
                          disabled={!currentManufacturerId || currentVehicleIds.length === 0}
                          style={{ width: "100%" }}
                        >
                          Add
                        </Button>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label={
                            <span className={!currentManufacturerId ? "text-gray-400" : undefined}>
                              Select Vehicles (Multiple)
                            </span>
                          }
                          style={{ marginBottom: 0 }}
                        >
                          <VehicleSelect
                            manufacturerId={currentManufacturerId}
                            segmentId={selectedSegmentId}
                            multiple={true}
                            value={currentVehicleIds}
                            onChange={(val, options) => {
                              setCurrentVehicleIds(Array.isArray(val) ? val : val ? [val] : []);
                              if (Array.isArray(options)) {
                                const map = {};
                                options.forEach((opt) => { map[opt.value] = opt.children; });
                                setVehicleOptionsMap(map);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>

                  {vehicleModelMappings.length === 0 ? (
                    <div className="border border-dashed rounded-lg p-5 text-center text-gray-400 text-sm">
                      No vehicles added yet. Select a manufacturer and vehicles above, then click <strong>Add</strong>.
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                        <span className="text-sm font-semibold text-gray-700">Added Mappings</span>
                        <Tag color="blue">
                          {vehicleModelMappings.length} row{vehicleModelMappings.length !== 1 ? "s" : ""}
                        </Tag>
                      </div>
                      <div className="flex flex-col divide-y">
                        {vehicleModelMappings.map((m, i) => {
                          const displayText = getModelDisplayText(m);
                          const modelTag = (
                            <Button
                              size="small"
                              type="dashed"
                              onClick={() => openModelEditModal(i)}
                              icon={<PencilIcon size={10} />}
                              style={{
                                margin: 0,
                                padding: "0 10px",
                                fontSize: 11,
                                height: 25,
                                lineHeight: "18px",
                                borderColor: displayText ? "#fa8c16" : "#52c41a",
                                color: displayText ? "#fa8c16" : "#52c41a",
                              }}
                            >
                              {displayText
                                ? displayText.length > 25
                                  ? displayText.slice(0, 25) + "…"
                                  : displayText
                                : "All Models"}
                            </Button>
                          );
                          return (
                            <div key={i} className="flex items-center justify-between bg-white px-3 py-2">
                              <div className="flex items-center gap-2 text-sm flex-1 min-w-0 flex-wrap">
                                <Tag color="blue" style={{ margin: 0 }}>
                                  {m.manufacturer_name || m.manufacturer_id}
                                </Tag>
                                <span className="text-gray-300">›</span>
                                <Tag color="purple" style={{ margin: 0 }}>
                                  {m.vehicle_name || m.vehicle_id}
                                </Tag>
                                <span className="text-gray-300">›</span>
                                {displayText && displayText.length > 25 ? (
                                  <Tooltip title={displayText}>{modelTag}</Tooltip>
                                ) : (
                                  modelTag
                                )}
                              </div>
                              <Tooltip title="Remove">
                                <button
                                  onClick={() => setVehicleModelMappings((prev) => prev.filter((_, idx) => idx !== i))}
                                  className="ml-3 shrink-0 flex items-center justify-center w-6 h-6 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-150"
                                >
                                  <DeleteOutlined style={{ fontSize: 13 }} />
                                </button>
                              </Tooltip>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Form>
          </>
        )}
      </Modal>

      {/* Model Selection Modal (nested) */}
      <Modal
        title={
          <div>
            <div>
              Select Models -{" "}
              <span className="text-purple-600">
                {vehicleModelMappings[modelEditRowIndex]?.vehicle_name ||
                  vehicleModelMappings[modelEditRowIndex]?.vehicle_id ||
                  ""}
              </span>
            </div>
          </div>
        }
        open={modelEditVisible}
        onOk={handleModelEditConfirm}
        onCancel={handleModelEditCancel}
        okText="Apply"
        destroyOnClose
        zIndex={1100}
        styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
      >
        {modelEditLoading ? (
          <div className="text-center py-8 text-gray-400">Loading models…</div>
        ) : modelEditModels.length === 0 ? (
          <div className="text-gray-400 py-8 text-center">No models found for this vehicle.</div>
        ) : (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Button type="link" size="small" style={{ padding: "0 4px" }} onClick={() => setModelEditSelected(modelEditModels.map((m) => m._id))}>
                  Select All
                </Button>
                <span className="text-gray-300">|</span>
                <Button type="link" size="small" style={{ padding: "0 4px" }} onClick={() => setModelEditSelected([])}>
                  Clear All
                </Button>
              </div>
              {modelEditSelected.length > 0 && <Tag color="orange" style={{ margin: 0 }}>{modelEditSelected.length} selected</Tag>}
            </div>
            <div style={{ paddingRight: 4 }}>
              <Checkbox.Group value={modelEditSelected} onChange={setModelEditSelected} style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                {modelEditModels.map((model) => (
                  <div key={model._id} className="flex items-center px-1 py-1 rounded hover:bg-gray-50">
                    <Checkbox value={model._id} style={{ width: "100%" }}>
                      {model.en_name}
                    </Checkbox>
                  </div>
                ))}
              </Checkbox.Group>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default IncompleteProducts;
